import algosdk from "algosdk";

import {MAXIMUM_PLATFORM_FEE_PERCENTAGE} from "./constants";
import {isPositiveNumber, parseAsFloat} from "./util/number/numberUtils";
import {SwapWidgetSearchParamKey, SwapWidgetToApplicationMessage} from "./widgetTypes";
import {hasAlreadyOptedIntoAsset, isValidAssetID} from "../asset/util/assetUtils";
import {fetchAccountAssetDetails} from "../wallet/util/walletUtils";
import {algod} from "./integrations/algosdk/AlgodManager";
import {generateSwapFormAssetIDs} from "../swap/form/SwapForm.utils";

/**
 * @returns the parent app url (decoded) from the URL search params, or `undefined` if there is none
 */
function getParentUrlFromSearchParams(): string | undefined {
  const searchParams = new URLSearchParams(document.location.search);
  const parentUrlEncoded = searchParams.get(SwapWidgetSearchParamKey.PARENT_URL_ORIGIN);
  const parentUrl = parentUrlEncoded
    ? Buffer.from(parentUrlEncoded, "base64").toString()
    : undefined;

  return parentUrl;
}

function getSelectableAssetsFromSearchParams(): number[] | undefined {
  const searchParams = new URLSearchParams(document.location.search);
  const selectableAssets = searchParams.get(SwapWidgetSearchParamKey.SELECTABLE_ASSETS);
  const {assetInID, assetOutID} = generateSwapFormAssetIDs();

  if (selectableAssets) {
    const selectableAssetArray = selectableAssets.split(",").map(Number);

    if (isValidAssetID(assetInID) && !selectableAssetArray.includes(assetInID)) {
      selectableAssetArray.push(assetInID);
    }

    if (isValidAssetID(assetOutID) && !selectableAssetArray.includes(assetOutID)) {
      selectableAssetArray.push(assetOutID);
    }

    return selectableAssetArray;
  }

  return undefined;
}

function sendMessageToParentApp(
  data: SwapWidgetToApplicationMessage,
  parentUrlOrigin: string | undefined
) {
  window.parent.postMessage(data, parentUrlOrigin ?? "*");
}

function getPlatformFeePercentage() {
  const searchParams = new URLSearchParams(document.location.search);
  const platformFeePercentage = parseAsFloat(
    searchParams.get(SwapWidgetSearchParamKey.PLATFORM_FEE_PERCENTAGE)
  );

  // eslint-disable-next-line no-magic-numbers
  return isPositiveNumber(platformFeePercentage) && platformFeePercentage < 100
    ? platformFeePercentage
    : null;
}

function getPlatformName() {
  const searchParams = new URLSearchParams(document.location.search);

  return searchParams.get(SwapWidgetSearchParamKey.PLATFORM_NAME) || "This platform";
}

async function getPlatformFeeError(assetIn: number, assetOut: number) {
  const searchParams = new URLSearchParams(document.location.search);
  const platformFeePercentage = parseAsFloat(
    searchParams.get(SwapWidgetSearchParamKey.PLATFORM_FEE_PERCENTAGE)
  );
  const platformFeeAccount = searchParams.get(
    SwapWidgetSearchParamKey.PLATFORM_FEE_ACCOUNT
  );
  const isAnyOfThePlatformFeeParamsProvided = Boolean(
    platformFeePercentage || platformFeeAccount
  );

  if (isAnyOfThePlatformFeeParamsProvided) {
    if (platformFeeAccount && !algosdk.isValidAddress(platformFeeAccount)) {
      return new Error("Platform fee account address is not valid");
    }

    if (
      !isPositiveNumber(platformFeePercentage) ||
      platformFeePercentage > MAXIMUM_PLATFORM_FEE_PERCENTAGE
    ) {
      return new Error(
        `Platform fee percentage must be a positive number less than or equal to ${MAXIMUM_PLATFORM_FEE_PERCENTAGE}`
      );
    }

    //  Make sure only 1 decimal point is provided at most for the platform fee percentage
    if (platformFeePercentage.toString().split(".")[1]?.length > 1) {
      return new Error("Platform fee percentage must have at most 1 decimal point");
    }

    if (!platformFeePercentage || !platformFeeAccount) {
      return new Error("Platform fee ratio and account must be provided together");
    }

    try {
      const accountInfo = await algod.client.accountInformation(platformFeeAccount).do();
      const accountAssets = await fetchAccountAssetDetails(accountInfo);

      if (!hasAlreadyOptedIntoAsset(accountAssets, assetIn)) {
        return new Error("Platform fee account is not opted-in to asset in");
      }

      if (!hasAlreadyOptedIntoAsset(accountAssets, assetOut)) {
        return new Error("Platform fee account is not opted-in to asset out");
      }
    } catch (error) {
      return new Error(
        "An error occured while trying to fetch assets of the platform fee account."
      );
    }
  }

  return null;
}

export {
  getParentUrlFromSearchParams,
  sendMessageToParentApp,
  getPlatformFeePercentage,
  getPlatformFeeError,
  getPlatformName,
  getSelectableAssetsFromSearchParams
};
